import { supabase } from "@/lib/supabaseClient";

export type PlanCode = "employer_starter" | "employer_growth" | "employer_enterprise";

export interface PlanDefinition {
  code: PlanCode;
  displayName: string;
  monthlyPriceCents: number;
  placementFeePercentage: number;
  jobPostingLimit: number | null;
  candidateViewLimit: number | null;
  sortOrder: number;
}

const PLAN_TABLE = "plan_definitions";

function mapRow(row: Record<string, unknown>): PlanDefinition {
  return {
    code: row.code as PlanCode,
    displayName: row.display_name as string,
    monthlyPriceCents: Number(row.monthly_price_cents),
    placementFeePercentage: Number(row.placement_fee_percentage),
    jobPostingLimit: row.job_posting_limit === null ? null : Number(row.job_posting_limit),
    candidateViewLimit: row.candidate_view_limit === null ? null : Number(row.candidate_view_limit),
    sortOrder: Number(row.sort_order ?? 0),
  };
}

export async function fetchPlanDefinitions(): Promise<PlanDefinition[]> {
  const { data, error } = await supabase
    .from(PLAN_TABLE)
    .select(
      [
        "code",
        "display_name",
        "monthly_price_cents",
        "placement_fee_percentage",
        "job_posting_limit",
        "candidate_view_limit",
        "sort_order",
      ].join(","),
    )
    .order("sort_order", { ascending: true });

  if (error) {
    throw new Error(`Unable to load plan definitions: ${error.message}`);
  }

  if (!data) return [];

  return data.map(mapRow);
}

const PLAN_CACHE = new Map<string, PlanDefinition>();

export async function getPlanDefinition(code: PlanCode): Promise<PlanDefinition | null> {
  if (PLAN_CACHE.has(code)) {
    return PLAN_CACHE.get(code)!;
  }

  const { data, error } = await supabase
    .from(PLAN_TABLE)
    .select(
      [
        "code",
        "display_name",
        "monthly_price_cents",
        "placement_fee_percentage",
        "job_posting_limit",
        "candidate_view_limit",
        "sort_order",
      ].join(","),
    )
    .eq("code", code)
    .maybeSingle();

  if (error) {
    throw new Error(`Unable to load plan ${code}: ${error.message}`);
  }

  if (!data) return null;

  const plan = mapRow(data);
  PLAN_CACHE.set(code, plan);
  return plan;
}

export function clearPlanCache() {
  PLAN_CACHE.clear();
}
